import React, { useState, useEffect } from "react";
import {
  BarChart3, GraduationCap, TrendingUp, Users, Activity,
  Hash, Building2, Wrench,
} from "lucide-react";
import theme from "../../styles/theme";
import { WizardHatIcon } from "../common/Brand";
import { fetchStats, fetchTrending } from "../../api";

const T = theme.colors;

function AboutCard() {
  return (
    <div style={{ background: theme.gradients.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <WizardHatIcon size={28} color={T.purple} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Wyzcon Intelligence</div>
          <div style={{ fontSize: 11, color: T.violet, fontFamily: theme.fonts.mono }}>Infrastructure Research</div>
        </div>
      </div>
      <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
        We build data infrastructure and research tools for educators.
        Every analysis is source-verified. Every tool is free.
        Teachers deserve transparency, not marketing decks.
      </p>
      <div style={{
        padding: "10px 14px", borderRadius: 8, background: T.surface,
        border: `1px solid ${T.border}`, fontSize: 12, color: T.textDim,
        fontFamily: theme.fonts.mono, lineHeight: 2,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Building2 size={13} color={T.violet} /> Infrastructure Research</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><BarChart3 size={13} color={T.accent} /> Policy Analysis</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Wrench size={13} color={T.green} /> Free Educator Tools</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Users size={13} color={T.blue} /> Community Intelligence</div>
      </div>
    </div>
  );
}

const STAT_META = [
  { key: "reports_published", label: "Reports Published", Icon: BarChart3, color: T.accent },
  { key: "educators_reached", label: "Educators Reached", Icon: GraduationCap, color: T.violet },
  { key: "data_points", label: "Data Points Tracked", Icon: TrendingUp, color: T.green },
  { key: "active_contributors", label: "Active Contributors", Icon: Users, color: T.blue },
];

function StatsCard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(console.error);
  }, []);

  if (!stats) return null;

  return (
    <div style={{ background: theme.gradients.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.accent, fontFamily: theme.fonts.mono, letterSpacing: 2, marginBottom: 16 }}>
        <Activity size={12} /> PLATFORM STATS
      </div>
      {STAT_META.map((s, i) => {
        const SIcon = s.Icon;
        const value = stats[s.key];
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i > 0 ? `1px solid ${T.border}40` : "none" }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: s.color + "15", border: `1px solid ${s.color}25`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SIcon size={18} color={s.color} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", fontFamily: theme.fonts.mono }}>{value}</div>
              <div style={{ fontSize: 11, color: T.textDim }}>{s.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendingCard() {
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetchTrending().then(setTags).catch(console.error);
  }, []);

  if (!tags.length) return null;

  return (
    <div style={{ background: theme.gradients.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.accent, fontFamily: theme.fonts.mono, letterSpacing: 2, marginBottom: 16 }}>
        <TrendingUp size={12} /> TRENDING TOPICS
      </div>
      {tags.map((t, i) => (
        <div key={t.tag} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: i > 0 ? `1px solid ${T.border}40` : "none", cursor: "pointer" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: T.text }}>
            <Hash size={12} color={T.textDim} />{t.tag}
          </span>
          <span style={{ fontSize: 11, color: T.textDim, fontFamily: theme.fonts.mono }}>{t.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside style={{ display: "flex", flexDirection: "column", gap: 20, position: "sticky", top: 80, alignSelf: "start" }}>
      <AboutCard />
      <StatsCard />
      <TrendingCard />
      <div style={{ fontSize: 10, color: T.textDim, fontFamily: theme.fonts.mono, lineHeight: 1.6, padding: "0 4px" }}>
        All comments are anonymous. Data sourced from public records.
        Wyzcon Intelligence is an independent research platform.
        Not affiliated with NYC DOE or UFT.
      </div>
    </aside>
  );
}
